import { fromBase64, sha256, toArrayBuffer, utf8, uuidv4 } from './base64.js';
const MAX_UNLOCK_ATTEMPTS = 10;
// Mock mirror of the server's named wipe constants (single source there). 72h for all three.
const WIPE_TOKEN_TTL_MS = 72 * 60 * 60 * 1000;
const WIPE_RESTORE_WINDOW_MS = 72 * 60 * 60 * 1000;
const WIPE_PURGE_CUTOFF_MS = 72 * 60 * 60 * 1000;
function ctEqual(a, b) {
    if (a.length !== b.length) {
        return false;
    }
    let d = 0;
    for (let i = 0; i < a.length; i += 1) {
        d |= a[i] ^ b[i];
    }
    return d === 0;
}
function concatBytes(a, b) {
    const out = new Uint8Array(a.length + b.length);
    out.set(a, 0);
    out.set(b, a.length);
    return out;
}
export class MockTransport {
    accounts = new Map();
    seedEmail = new Map();
    // Soft-deleted accounts awaiting restore or purge (72h window). Most-recent-last.
    wipedSnapshots = [];
    clock;
    auditSeq = 1;
    constructor(opts = {}) {
        // Logical clock for strictly-increasing updatedAt cursors.
        this.clock = Date.now();
        for (const id of opts.identities ?? []) {
            this.seedEmail.set(id.sub, id.email);
        }
    }
    /** Strictly-increasing ISO timestamp (sync-cursor safe). */
    tick() {
        this.clock += 1;
        return new Date(this.clock).toISOString();
    }
    /** Inspect stored state — tests assert no plaintext leaked here. */
    dump() {
        return JSON.stringify([...this.accounts.values()].map((a) => ({
            userId: a.userId,
            envelopes: [...a.envelopes.values()],
            vaults: [...a.vaults.values()],
            items: [...a.items.values()],
            umkProofKey: a.umkProofKey ? '[bytes]' : null,
        })));
    }
    /**
     * Test-only: the raw wipe token the server would have emailed out-of-band. The HTTP
     * `wipe/start` response never carries it (that is the point of this increment), so a test that
     * needs to drive `wipe/confirm` reads it from here, exactly as a user would read it from email.
     */
    pendingWipeToken(sub) {
        const acct = this.accounts.get(sub);
        if (acct === undefined) {
            return undefined;
        }
        let found;
        for (const [token, wt] of acct.wipeTokens) {
            if (wt.status === 'pending') {
                found = token;
            }
        }
        return found;
    }
    /** Mirror of the server's `purgeExpiredWipes()`: drop soft-deleted snapshots past the cutoff. */
    purgeExpiredWipes() {
        const cutoff = this.clock - WIPE_PURGE_CUTOFF_MS;
        for (let i = this.wipedSnapshots.length - 1; i >= 0; i -= 1) {
            const s = this.wipedSnapshots[i];
            if (Date.parse(s.wipedAt) <= cutoff) {
                this.wipedSnapshots.splice(i, 1);
            }
        }
    }
    async request(req) {
        try {
            return await this.route(req);
        }
        catch (e) {
            const message = e instanceof Error ? e.message : 'mock error';
            return this.err(500, 'internal', message);
        }
    }
    // ---- helpers --------------------------------------------------------------
    ok(json, status = 200) {
        return { status, json, headers: {} };
    }
    err(status, code, message) {
        return { status, json: { error: { code, message } }, headers: {} };
    }
    subOf(req) {
        const auth = req.headers['authorization'];
        if (auth === undefined || !auth.startsWith('Bearer ')) {
            return null;
        }
        return auth.slice('Bearer '.length);
    }
    account(sub) {
        let acct = this.accounts.get(sub);
        if (acct === undefined) {
            acct = {
                userId: '',
                sub,
                email: this.seedEmail.get(sub) ?? `${sub}@example.test`,
                createdAt: this.tick(),
                enrolled: false,
                recoveryCodeAcknowledgedAt: null,
                lockedAt: null,
                saltPass: null,
                saltRec: null,
                params: null,
                recoveryCodeVerifier: null,
                umkProofKey: null,
                envelopes: new Map(),
                vaults: new Map(),
                items: new Map(),
                devices: new Map(),
                pairing: new Map(),
                wipeTokens: new Map(),
                audit: [],
                usedNonces: new Set(),
                failedUnlocks: 0,
                thisDeviceId: null,
            };
            this.accounts.set(sub, acct);
        }
        return acct;
    }
    async verifyUmkProof(acct, req) {
        const nonce = req.headers['x-umk-nonce'];
        const proofB64 = req.headers['x-umk-proof'];
        if (nonce === undefined || proofB64 === undefined || acct.umkProofKey === null) {
            return false;
        }
        if (acct.usedNonces.has(nonce)) {
            return false; // replay
        }
        const bodyHash = await sha256(utf8(req.bodyText ?? ''));
        const message = concatBytes(utf8(`${req.method}:${req.path}:${nonce}:`), bodyHash);
        const key = await crypto.subtle.importKey('raw', toArrayBuffer(acct.umkProofKey), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
        const expected = new Uint8Array(await crypto.subtle.sign('HMAC', key, toArrayBuffer(message)));
        if (!ctEqual(expected, fromBase64(proofB64))) {
            return false;
        }
        acct.usedNonces.add(nonce);
        return true;
    }
    meOf(acct) {
        return {
            userId: acct.userId,
            heivolIdSub: acct.sub,
            email: acct.email,
            enrolled: acct.enrolled,
            plan: 'free',
            recoveryCodeAcknowledgedAt: acct.recoveryCodeAcknowledgedAt,
            lockedAt: acct.lockedAt,
            createdAt: acct.createdAt,
        };
    }
    deviceOf(acct, d) {
        return { ...d, thisDevice: d.id === acct.thisDeviceId };
    }
    vaultOf(acct, v) {
        let itemCount = 0;
        for (const it of acct.items.values()) {
            if (it.vaultId === v.id && it.deletedAt === null) {
                itemCount += 1;
            }
        }
        return {
            id: v.id,
            nameBlob: v.nameBlob,
            wrappedDek: v.wrappedDek,
            createdAt: v.createdAt,
            itemCount,
        };
    }
    itemOf(it) {
        return { ...it };
    }
    audit(acct, event, metadata) {
        acct.audit.push({
            id: String(this.auditSeq++),
            event: event,
            metadata,
            actor: 'user',
            reason: null,
            at: this.tick(),
        });
    }
    // ---- router ---------------------------------------------------------------
    async route(req) {
        const sub = this.subOf(req);
        if (sub === null) {
            return this.err(401, 'unauthorized', 'missing bearer token');
        }
        const acct = this.account(sub);
        const [pathOnly, queryString] = req.path.split('?');
        const query = new URLSearchParams(queryString ?? '');
        const segs = (pathOnly ?? '').split('/').filter((s) => s.length > 0); // ['v1','items',':id']
        const body = req.bodyText !== undefined ? JSON.parse(req.bodyText) : undefined;
        // umk-proof gate for protected routes
        const needsProof = this.routeNeedsProof(req.method, segs);
        if (needsProof && !(await this.verifyUmkProof(acct, req))) {
            return this.err(401, 'umk_proof_invalid', 'umk-proof missing, invalid, or replayed');
        }
        const m = req.method;
        const p = segs.join('/');
        // 3 GET /v1/me
        if (m === 'GET' && p === 'v1/me') {
            return this.ok(this.meOf(acct));
        }
        // 1 POST /v1/enroll
        if (m === 'POST' && p === 'v1/enroll') {
            return this.handleEnroll(acct, body);
        }
        // 2 POST /v1/enroll/recovery-code/ack
        if (m === 'POST' && p === 'v1/enroll/recovery-code/ack') {
            acct.recoveryCodeAcknowledgedAt = this.tick();
            return this.ok({ recoveryCodeAcknowledgedAt: acct.recoveryCodeAcknowledgedAt });
        }
        // 4 GET /v1/unlock/envelopes
        if (m === 'GET' && p === 'v1/unlock/envelopes') {
            const env = acct.envelopes.get('passphrase');
            if (!acct.enrolled || env === undefined || acct.saltPass === null || acct.params === null) {
                return this.err(404, 'not_enrolled', 'no passphrase envelope');
            }
            return this.ok({
                userId: acct.userId,
                passphraseEnvelope: env,
                argon2SaltPass: acct.saltPass,
                params: acct.params,
            });
        }
        // 5 POST /v1/unlock/attempt
        if (m === 'POST' && p === 'v1/unlock/attempt') {
            const out = body.outcome;
            if (out === 'success') {
                acct.failedUnlocks = 0;
                acct.lockedAt = null;
                this.audit(acct, 'unlock.pass', null);
            }
            else {
                acct.failedUnlocks += 1;
                if (acct.failedUnlocks >= MAX_UNLOCK_ATTEMPTS) {
                    acct.lockedAt = this.tick();
                }
            }
            return this.ok({
                remainingAttempts: Math.max(0, MAX_UNLOCK_ATTEMPTS - acct.failedUnlocks),
                lockedUntil: acct.lockedAt,
            });
        }
        // 6 POST /v1/recovery/start
        if (m === 'POST' && p === 'v1/recovery/start') {
            const env = acct.envelopes.get('recovery');
            if (env === undefined || acct.saltRec === null || acct.params === null) {
                return this.err(404, 'not_enrolled', 'no recovery envelope');
            }
            this.audit(acct, 'recovery.start', null);
            this.clock += 72 * 3600 * 1000;
            return this.ok({
                userId: acct.userId,
                recoveryEnvelope: env,
                argon2SaltRec: acct.saltRec,
                params: acct.params,
                cooldownUntil: new Date(this.clock).toISOString(),
            });
        }
        // 7 POST /v1/recovery/complete
        if (m === 'POST' && p === 'v1/recovery/complete') {
            return this.handleRecoveryComplete(acct, body);
        }
        // 8 POST /v1/passphrase/rotate
        if (m === 'POST' && p === 'v1/passphrase/rotate') {
            const b = body;
            acct.envelopes.set('passphrase', b.newPassphraseEnvelope);
            acct.saltPass = b.argon2SaltPass;
            acct.usedNonces.clear(); // other sessions invalidated
            this.audit(acct, 'passphrase.rotate', null);
            return this.ok({ ok: true });
        }
        // 9 GET /v1/vaults
        if (m === 'GET' && p === 'v1/vaults') {
            return this.ok([...acct.vaults.values()].map((v) => this.vaultOf(acct, v)));
        }
        // 10 POST /v1/vaults
        if (m === 'POST' && p === 'v1/vaults') {
            const b = body;
            const v = {
                id: b.id,
                nameBlob: b.nameBlob,
                wrappedDek: b.wrappedDek,
                createdAt: this.tick(),
            };
            acct.vaults.set(v.id, v);
            this.audit(acct, 'vault.write', { vaultId: v.id, op: 'create' });
            return this.ok({ vault: this.vaultOf(acct, v) }, 201);
        }
        // 11/12 /v1/vaults/:id
        if (segs.length === 3 && segs[1] === 'vaults') {
            const id = segs[2];
            const v = acct.vaults.get(id);
            if (v === undefined) {
                return this.err(404, 'not_found', 'vault not found');
            }
            if (m === 'PATCH') {
                const b = body;
                v.nameBlob = b.nameBlob;
                v.wrappedDek = b.wrappedDek;
                this.audit(acct, 'vault.write', { vaultId: id, op: 'rename' });
                return this.ok({ vault: this.vaultOf(acct, v) });
            }
            if (m === 'DELETE') {
                for (const it of acct.items.values()) {
                    if (it.vaultId === id && it.deletedAt === null) {
                        it.deletedAt = this.tick();
                    }
                }
                acct.vaults.delete(id);
                this.audit(acct, 'vault.write', { vaultId: id, op: 'delete' });
                return this.ok({ ok: true });
            }
        }
        // 18 GET /v1/items/lookup
        if (m === 'GET' && p === 'v1/items/lookup') {
            const dh = query.get('domain_hash');
            const items = [...acct.items.values()].filter((it) => it.deletedAt === null && it.domainHash === dh);
            return this.ok({ items: items.map((it) => this.itemOf(it)) });
        }
        // 13 GET /v1/items?vault_id=&since=
        if (m === 'GET' && p === 'v1/items') {
            const vaultId = query.get('vault_id');
            const since = query.get('since');
            let items = [...acct.items.values()];
            if (vaultId !== null) {
                items = items.filter((it) => it.vaultId === vaultId);
            }
            if (since !== null) {
                items = items.filter((it) => it.updatedAt > since); // includes soft-deletes for sync
            }
            else {
                items = items.filter((it) => it.deletedAt === null);
            }
            items.sort((a, b) => (a.updatedAt < b.updatedAt ? -1 : 1));
            const cursor = items.length > 0 ? items[items.length - 1].updatedAt : null;
            return this.ok({ items: items.map((it) => this.itemOf(it)), cursor });
        }
        // 14 POST /v1/items
        if (m === 'POST' && p === 'v1/items') {
            const b = body;
            const it = {
                id: b.id,
                vaultId: b.vaultId,
                kind: b.kind,
                domainHash: b.domainHash,
                wrappedDek: b.wrappedDek,
                ciphertext: b.ciphertext,
                version: 1,
                updatedAt: this.tick(),
                deletedAt: null,
            };
            acct.items.set(it.id, it);
            this.audit(acct, 'item.write', { itemId: it.id, op: 'create' });
            return this.ok({ item: this.itemOf(it) }, 201);
        }
        // 15/16/17 /v1/items/:id
        if (segs.length === 3 && segs[1] === 'items') {
            const id = segs[2];
            const it = acct.items.get(id);
            if (it === undefined || it.deletedAt !== null) {
                return this.err(404, 'not_found', 'item not found');
            }
            if (m === 'GET') {
                this.audit(acct, 'item.read', { itemId: id });
                return this.ok({ item: this.itemOf(it) });
            }
            if (m === 'PATCH') {
                const b = body;
                if (b.expectedVersion !== it.version) {
                    return this.err(409, 'conflict', 'version conflict');
                }
                it.kind = b.kind;
                it.domainHash = b.domainHash;
                it.wrappedDek = b.wrappedDek;
                it.ciphertext = b.ciphertext;
                it.version += 1;
                it.updatedAt = this.tick();
                this.audit(acct, 'item.write', { itemId: id, op: 'update' });
                return this.ok({ item: this.itemOf(it) });
            }
            if (m === 'DELETE') {
                const ts = this.tick();
                it.deletedAt = ts;
                it.version += 1;
                it.updatedAt = ts;
                this.audit(acct, 'item.write', { itemId: id, op: 'delete' });
                return this.ok({ ok: true });
            }
        }
        // 19 GET /v1/devices
        if (m === 'GET' && p === 'v1/devices') {
            return this.ok([...acct.devices.values()].map((d) => this.deviceOf(acct, d)));
        }
        // 20 POST /v1/devices/pair/init
        if (m === 'POST' && p === 'v1/devices/pair/init') {
            const b = body;
            const token = uuidv4();
            this.clock += 10 * 60 * 1000;
            const pr = {
                id: uuidv4(),
                newDeviceName: b.newDeviceName,
                newDevicePublicKey: b.newDevicePublicKey,
                token,
                status: 'pending',
                createdAt: this.tick(),
                expiresAt: new Date(this.clock).toISOString(),
                deviceEnvelope: null,
            };
            acct.pairing.set(token, pr);
            return this.ok({ token, expiresAt: pr.expiresAt });
        }
        // 21 GET /v1/devices/pair/pending
        if (m === 'GET' && p === 'v1/devices/pair/pending') {
            const pending = [...acct.pairing.values()].find((pr) => pr.status === 'pending') ?? null;
            return this.ok({
                request: pending === null
                    ? null
                    : {
                        id: pending.id,
                        newDeviceName: pending.newDeviceName,
                        newDevicePublicKey: pending.newDevicePublicKey,
                        token: pending.token,
                        status: pending.status,
                        createdAt: pending.createdAt,
                        expiresAt: pending.expiresAt,
                    },
            });
        }
        // 22/23 /v1/devices/pair/:token/(approve|claim)
        if (segs.length === 5 && segs[1] === 'devices' && segs[2] === 'pair') {
            const token = segs[3];
            const action = segs[4];
            const pr = acct.pairing.get(token);
            if (pr === undefined) {
                return this.err(404, 'not_found', 'pairing not found');
            }
            if (m === 'POST' && action === 'approve') {
                const b = body;
                const d = {
                    id: b.deviceId,
                    name: b.deviceName,
                    kind: b.deviceKind,
                    publicKey: pr.newDevicePublicKey,
                    enrolledAt: this.tick(),
                    lastSeenAt: null,
                    revokedAt: null,
                };
                acct.devices.set(d.id, d);
                acct.envelopes.set(`device:${d.id}`, b.deviceEnvelope);
                pr.status = 'approved';
                pr.deviceEnvelope = b.deviceEnvelope;
                this.audit(acct, 'device.enroll', { deviceId: d.id });
                return this.ok({ device: this.deviceOf(acct, d) });
            }
            if (m === 'POST' && action === 'claim') {
                return this.ok({ status: pr.status, deviceEnvelope: pr.deviceEnvelope });
            }
        }
        // 24 DELETE /v1/devices/:id
        if (m === 'DELETE' && segs.length === 3 && segs[1] === 'devices') {
            const id = segs[2];
            const d = acct.devices.get(id);
            if (d === undefined) {
                return this.err(404, 'not_found', 'device not found');
            }
            d.revokedAt = this.tick();
            acct.envelopes.delete(`device:${id}`);
            this.audit(acct, 'device.revoke', { deviceId: id });
            return this.ok({ ok: true });
        }
        // 25 GET /v1/audit
        if (m === 'GET' && p === 'v1/audit') {
            const since = query.get('since');
            const entries = acct.audit.filter((e) => since === null || e.at > since);
            const cursor = entries.length > 0 ? entries[entries.length - 1].at : null;
            return this.ok({ entries, cursor });
        }
        // 26 DELETE /v1/account
        if (m === 'DELETE' && p === 'v1/account') {
            acct.enrolled = false;
            acct.envelopes.clear();
            acct.vaults.clear();
            acct.items.clear();
            acct.devices.clear();
            acct.pairing.clear();
            acct.umkProofKey = null;
            this.audit(acct, 'account.delete', null);
            return this.ok({ ok: true });
        }
        // 27 POST /v1/wipe/start — jwt only (no unlock; that's the point). The server emails a signed
        // link out-of-band and returns NO token in the body ({ ok, expiresAt } only). The mock keeps
        // the token internally so a test can read it via `pendingWipeToken()`, as a user reads email.
        if (m === 'POST' && p === 'v1/wipe/start') {
            this.purgeExpiredWipes();
            if (!acct.enrolled) {
                return this.err(404, 'not_enrolled', 'not enrolled');
            }
            const token = uuidv4();
            // 72h open TTL. Offset off the logical clock WITHOUT advancing it, so later ticks stay well
            // inside the window (advancing the clock to the deadline would expire it on the next tick).
            const expiresAt = new Date(this.clock + WIPE_TOKEN_TTL_MS).toISOString();
            acct.wipeTokens.set(token, { status: 'pending', expiresAt });
            this.audit(acct, 'wipe.start', null);
            return this.ok({ ok: true, expiresAt });
        }
        // 28 POST /v1/wipe/confirm — jwt + single-use token + typed `WIPE`. NO umk-proof by design.
        // SOFT-deletes: the enrolled state is snapshotted for a 72h restore window, then the account
        // is un-enrolled in place so re-enrolment under the same sub mints a fresh vault.
        if (m === 'POST' && p === 'v1/wipe/confirm') {
            const b = body;
            const wt = b.token !== undefined ? acct.wipeTokens.get(b.token) : undefined;
            const now = new Date(this.clock).toISOString();
            if (wt === undefined || wt.status !== 'pending' || wt.expiresAt < now) {
                return this.err(404, 'not_found', 'wipe token invalid, used, or expired');
            }
            if (b.confirm !== 'WIPE') {
                return this.err(400, 'bad_request', "type 'WIPE' to confirm");
            }
            wt.status = 'confirmed';
            this.wipedSnapshots.push({
                sub: acct.sub,
                wipedAt: this.tick(),
                userId: acct.userId,
                saltPass: acct.saltPass,
                saltRec: acct.saltRec,
                params: acct.params,
                recoveryCodeVerifier: acct.recoveryCodeVerifier,
                recoveryCodeAcknowledgedAt: acct.recoveryCodeAcknowledgedAt,
                lockedAt: acct.lockedAt,
                umkProofKey: acct.umkProofKey,
                envelopes: new Map(acct.envelopes),
                vaults: new Map(acct.vaults),
                items: new Map(acct.items),
                devices: new Map(acct.devices),
                pairing: new Map(acct.pairing),
                thisDeviceId: acct.thisDeviceId,
                failedUnlocks: acct.failedUnlocks,
            });
            acct.enrolled = false;
            acct.userId = '';
            acct.saltPass = null;
            acct.saltRec = null;
            acct.params = null;
            acct.recoveryCodeVerifier = null;
            acct.recoveryCodeAcknowledgedAt = null;
            acct.umkProofKey = null;
            acct.envelopes = new Map();
            acct.vaults = new Map();
            acct.items = new Map();
            acct.devices = new Map();
            acct.pairing = new Map();
            acct.usedNonces.clear();
            acct.wipeTokens.clear();
            acct.failedUnlocks = 0;
            acct.lockedAt = null;
            acct.thisDeviceId = null;
            this.audit(acct, 'wipe.complete', null);
            return this.ok({ ok: true });
        }
        // 29 POST /v1/wipe/cancel — token-free, all-device. Cancel a pending request for the live
        // user, else restore the most recent soft-deleted account inside the 72h window. A cancel over
        // an account that has since re-enrolled is a clean 409 ("already set up a new vault").
        if (m === 'POST' && p === 'v1/wipe/cancel') {
            let pendingCancelled = false;
            for (const wt of acct.wipeTokens.values()) {
                if (wt.status === 'pending') {
                    wt.status = 'cancelled';
                    pendingCancelled = true;
                }
            }
            if (pendingCancelled) {
                this.audit(acct, 'wipe.cancel', null);
                return this.ok({ ok: true });
            }
            const windowStart = this.clock - WIPE_RESTORE_WINDOW_MS;
            let idx = -1;
            for (let i = this.wipedSnapshots.length - 1; i >= 0; i -= 1) {
                const s = this.wipedSnapshots[i];
                if (s.sub === acct.sub && Date.parse(s.wipedAt) > windowStart) {
                    idx = i;
                    break;
                }
            }
            if (idx === -1) {
                return this.err(404, 'not_found', 'no wipe to cancel or restore');
            }
            if (acct.enrolled) {
                return this.err(409, 'conflict', 'already set up a new vault');
            }
            const snap = this.wipedSnapshots.splice(idx, 1)[0];
            acct.userId = snap.userId;
            acct.enrolled = true;
            acct.saltPass = snap.saltPass;
            acct.saltRec = snap.saltRec;
            acct.params = snap.params;
            acct.recoveryCodeVerifier = snap.recoveryCodeVerifier;
            acct.recoveryCodeAcknowledgedAt = snap.recoveryCodeAcknowledgedAt;
            acct.lockedAt = snap.lockedAt;
            acct.umkProofKey = snap.umkProofKey;
            acct.envelopes = snap.envelopes;
            acct.vaults = snap.vaults;
            acct.items = snap.items;
            acct.pairing = snap.pairing;
            acct.devices = snap.devices;
            acct.thisDeviceId = snap.thisDeviceId;
            acct.failedUnlocks = snap.failedUnlocks;
            this.audit(acct, 'wipe.restore', null);
            return this.ok({ ok: true });
        }
        return this.err(404, 'not_found', `no route for ${m} ${pathOnly}`);
    }
    routeNeedsProof(method, segs) {
        const p = segs.join('/');
        if (p === 'v1/passphrase/rotate')
            return true;
        if (p === 'v1/vaults' && method !== 'GET')
            return true;
        if (segs.length === 3 && segs[1] === 'vaults' && method !== 'GET')
            return true;
        if (p === 'v1/items' || p === 'v1/items/lookup')
            return true;
        if (segs.length === 3 && segs[1] === 'items')
            return true;
        if (p === 'v1/devices/pair/pending')
            return true;
        if (segs.length === 5 && segs[1] === 'devices' && segs[2] === 'pair' && segs[4] === 'approve') {
            return true;
        }
        if (method === 'DELETE' && segs.length === 3 && segs[1] === 'devices')
            return true;
        if (method === 'DELETE' && p === 'v1/account')
            return true;
        return false;
    }
    handleEnroll(acct, body) {
        if (acct.enrolled) {
            return this.err(409, 'already_enrolled', 'account already enrolled');
        }
        acct.userId = body.userId;
        acct.enrolled = true;
        acct.saltPass = body.argon2SaltPass;
        acct.saltRec = body.argon2SaltRec;
        acct.params = body.params;
        acct.recoveryCodeVerifier = body.recoveryCodeVerifier;
        acct.umkProofKey = fromBase64(body.umkProofKey);
        acct.envelopes.set('passphrase', body.passphraseEnvelope);
        acct.envelopes.set('recovery', body.recoveryEnvelope);
        const d = {
            id: body.device.id,
            name: body.device.name,
            kind: body.device.kind,
            publicKey: body.device.publicKey,
            enrolledAt: this.tick(),
            lastSeenAt: this.tick(),
            revokedAt: null,
        };
        acct.devices.set(d.id, d);
        acct.thisDeviceId = d.id;
        this.audit(acct, 'device.enroll', { deviceId: d.id });
        return this.ok({ me: this.meOf(acct), device: this.deviceOf(acct, d) }, 201);
    }
    handleRecoveryComplete(acct, body) {
        const b = body;
        if (acct.recoveryCodeVerifier === null || b.recoveryProof !== acct.recoveryCodeVerifier) {
            return this.err(403, 'forbidden', 'recovery proof mismatch');
        }
        acct.envelopes.set('passphrase', b.newPassphraseEnvelope);
        acct.saltPass = b.argon2SaltPass;
        acct.umkProofKey = fromBase64(b.umkProofKey);
        acct.usedNonces.clear();
        acct.failedUnlocks = 0;
        acct.lockedAt = null;
        this.audit(acct, 'recovery.complete', null);
        return this.ok({ me: this.meOf(acct) });
    }
}
//# sourceMappingURL=mock-transport.js.map